/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/UserProfile.tsx
 * DESCRIPTION: Enhanced user profile management with industry selection,
 *              onboarding tracking, and organization support via Clerk.
 * 
 * IMPORTS IN:
 * - src/App.tsx (Route: /profile)
 * 
 * FEATURES:
 * - Industry/business sector selection for tailored AI assessments
 * - Onboarding status tracking via Clerk unsafeMetadata (Beta)
 * - Organization membership display (read-only initially)
 * - Email change restriction based on onboarding completion
 * - Custom profile details section with edit/save/cancel functionality
 * - Full dark mode support and mobile responsiveness
 * - Frontend-only implementation (no backend changes required for Beta)
 * - 30-day industry immutability rule (overridable by Admin/Enterprise)
 * - Dedicated Onboarding Wizard for new users
 * 
 * SECURITY NOTE:
 * - Currently uses unsafeMetadata for frontend-writable fields during Beta
 * - Will migrate to publicMetadata via Go Backend API in Production
 * 
 * ROADMAP STATUS:
 * - Phase 1: Industry selection + onboarding tracking (Complete)
 * - Phase 2: Organization creation/joining flow (In Progress - Read-only view)
 * - Phase 3: Mandatory onboarding enforcement (Wizard implemented)
 * - Phase 4: Industry lock (30-day immutability after selection) (Implemented)
 * - Phase 5: Migration to Supabase + Go Backend for production ⏳ (Pending)
 *   - Move industry/onboarding to publicMetadata (backend-controlled)
 *   - Enable user filtering by industry/sector for admin analytics
 *   - Implement organization-based data segregation with RLS
 *   - Add rich profile fields, preferences, and analytics
 * 
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.5.0
 * @created 2025-01-15
 * @updated 2026-08-25 - Integrated OnboardingWizard, implemented 30-day
 *                       industry locking with license overrides, added
 *                       custom profile details editing, and updated
 *                       roadmap documentation.
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react'
import { useUser, useOrganizationList } from '@clerk/react'
import { Building2, Briefcase, CheckCircle, AlertCircle, User, Mail, Save, X, Lock, ArrowRight } from 'lucide-react'
import OnboardingWizard from './Auth/OnboardingWizard'

// Industry sectors matching backend API prompts
const INDUSTRY_SECTORS = [
  { id: 'commercial_product', label: 'Commercial / Product Image' },
  { id: 'defense_intelligence', label: 'Defense & Intelligence' },
  { id: 'financial_services', label: 'Financial Institutions / KYC-AML' },
  { id: 'government_public', label: 'Government & Public Sector' },
  { id: 'human_resources', label: 'Human Resources / KYE' },
  { id: 'identity_document', label: 'Identity / KYC Document' },
  { id: 'legal_ediscovery', label: 'Legal & E-Discovery' },
  { id: 'news_media', label: 'News & Media Editorial' },
  { id: 'platforms_marketplaces', label: 'Platforms & Marketplaces' },
  { id: 'political_content', label: 'Political Campaigns & Messaging' },
  { id: '', label: 'Generic Forensic Analysis' },
  { id: 'dating_profile', label: 'Dating Profile Picture' },
  { id: 'artistic_creative', label: 'Artistic / Creative Work' },
] as const

type IndustrySector = typeof INDUSTRY_SECTORS[number]['id']

interface UserMetadata {
  industry?: IndustrySector
  onboarding_completed?: boolean
  industry_set_at?: number
  organization_name?: string
  role?: string
  license?: string
}

/**
 * UserProfile Component
 * Enhanced profile page with industry selection and onboarding tracking.
 * Uses Clerk's unsafeMetadata for Beta phase (frontend-writable).
 * 
 * @returns {JSX.Element} User profile management interface
 */
const UserProfile: React.FC = () => {
  const { user, isLoaded: isUserLoaded } = useUser()
  const { userMemberships, isLoaded: isOrgLoaded } = useOrganizationList({
    userMemberships: true,
  })
  
  // Profile details state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false)
  const [profileSaveError, setProfileSaveError] = useState('')
  
  // Industry selection state
  const [selectedIndustry, setSelectedIndustry] = useState<IndustrySector>('')
  const [isSavingIndustry, setIsSavingIndustry] = useState(false)
  const [industrySaveSuccess, setIndustrySaveSuccess] = useState(false)
  const [industrySaveError, setIndustrySaveError] = useState('')
  
  // Onboarding status
  const [onboardingComplete, setOnboardingComplete] = useState(false)
  const [showWizard, setShowWizard] = useState(false)

  // Load current user data on mount
  useEffect(() => {
    if (isUserLoaded && user) {
      const metadata = user.unsafeMetadata as UserMetadata
      setFirstName(user.firstName || '')
      setLastName(user.lastName || '')
      setSelectedIndustry(metadata.industry || '')
      setOnboardingComplete(metadata.onboarding_completed || false)
      
      // Show wizard if onboarding is not complete
      if (!metadata.onboarding_completed) {
        setShowWizard(true)
      }
    }
  }, [isUserLoaded, user])

  /**
   * Helper to check if industry can be edited
   * Implements 30-day lock with Admin/Enterprise override
   */
  const canEditIndustry = () => {
    const metadata = user?.unsafeMetadata as UserMetadata
    if (!metadata?.industry_set_at) return true // Never set, so editable
    
    const license = metadata?.license
    const role = metadata?.role
    
    // Admins and Enterprise/Business users can always edit
    if (role === 'admin' || license === 'enterprise' || license === 'business') {
      return true
    }

    // Check 30-day lock (30 days in milliseconds)
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000
    const timeSinceSet = Date.now() - (metadata.industry_set_at || 0)
    
    return timeSinceSet > thirtyDaysInMs
  }

  /**
   * Save profile details (first name, last name) via Clerk
   */
  const handleSaveProfile = async () => {
    if (!user) return
    
    setIsSavingProfile(true)
    setProfileSaveError('')
    setProfileSaveSuccess(false)

    try {
      await user.update({
        firstName,
        lastName,
      })
      
      setProfileSaveSuccess(true)
      setIsEditingProfile(false)
      
      setTimeout(() => setProfileSaveSuccess(false), 3000)
    } catch (error: any) {
      setProfileSaveError(error.message || 'Failed to save profile details')
    } finally {
      setIsSavingProfile(false)
    }
  }

  /**
   * Cancel profile editing and restore original values
   */
  const handleCancelProfileEdit = () => {
    if (user) {
      setFirstName(user.firstName || '')
      setLastName(user.lastName || '')
    }
    setIsEditingProfile(false)
    setProfileSaveError('')
  }

  /**
   * Save industry selection via Clerk Frontend API (unsafeMetadata)
   */
  const handleSaveIndustry = async () => {
    if (!user) return
    
    setIsSavingIndustry(true)
    setIndustrySaveError('')
    setIndustrySaveSuccess(false)

    try {
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          industry: selectedIndustry,
          onboarding_completed: true,
          industry_set_at: Date.now(),
        }
      })
      
      setOnboardingComplete(true)
      setIndustrySaveSuccess(true)
      
      setTimeout(() => setIndustrySaveSuccess(false), 3000)
    } catch (error: any) {
      setIndustrySaveError(error.message || 'Failed to save industry selection')
    } finally {
      setIsSavingIndustry(false)
    }
  }

  /**
   * Check if email changes should be allowed
   */
  const canChangeEmail = onboardingComplete

  // Get organization memberships from the paginated resource
  const memberships = userMemberships?.data || []

  if (!isUserLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <>
      {/* Onboarding Wizard Modal */}
      {showWizard && !onboardingComplete && (
        <OnboardingWizard 
          redirectPath="/analyze"
          onComplete={() => {
            setOnboardingComplete(true)
            setShowWizard(false)
            // Redirect to analyze page immediately
            window.location.href = '/analyze'
          }} 
        />
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">Profile Settings</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Manage your account settings and preferences</p>
          </div>

          {/* Onboarding Status Banner */}
          {!onboardingComplete && (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                  Complete your profile setup
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  Select your industry to receive tailored media assessments and unlock all features.
                </p>
              </div>
            </div>
          )}

          {onboardingComplete && (
            <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-emerald-900 dark:text-emerald-300">
                    Profile setup complete
                  </h3>
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
                    Your industry preference is saved. You are ready to start analyzing media.
                  </p>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/analyze'}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Start Analysis
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Profile Details Card - Custom Implementation */}
          <div className="card mb-6">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Profile Details
                  </h2>
                </div>
                {!isEditingProfile && (
                  <button
                    onClick={() => setIsEditingProfile(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Edit
                  </button>
                )}
              </div>

              {profileSaveSuccess && (
                <div className="mb-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Profile details updated successfully
                </div>
              )}

              {profileSaveError && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {profileSaveError}
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name
                    </label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="John"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white">
                        {firstName || 'Not set'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name
                    </label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Doe"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white">
                        {lastName || 'Not set'}
                      </div>
                    )}
                  </div>
                </div>

                {isEditingProfile && (
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSavingProfile}
                      className="flex-1 sm:flex-none px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {isSavingProfile ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancelProfileEdit}
                      className="flex-1 sm:flex-none px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Email Address Card */}
          <div className="card mb-6">
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Email Address
                </h2>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Primary Email
                </label>
                <div className={`px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg ${!canChangeEmail ? 'opacity-50' : ''}`}>
                  <p className="text-gray-900 dark:text-white">{user.primaryEmailAddress?.emailAddress}</p>
                  {!canChangeEmail && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Complete your profile setup to enable email changes
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Industry Selection Card */}
          <div className="card mb-6">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Industry & Use Case
                  </h2>
                </div>
                {!canEditIndustry() && (
                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full border border-gray-200 dark:border-gray-600 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Locked for 30 days
                  </span>
                )}
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select your primary industry to receive tailored AI assessments. 
                {canEditIndustry() 
                  ? " You can update this anytime." 
                  : " Changes are restricted for 30 days after selection to ensure consistent model training."}
              </p>

              {industrySaveSuccess && (
                <div className="mb-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Industry preference saved successfully
                </div>
              )}

              {industrySaveError && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {industrySaveError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Primary Industry / Sector
                  </label>
                  <select
                    id="industry"
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value as IndustrySector)}
                    disabled={!canEditIndustry()}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
                  >
                    <option value="">Select your industry...</option>
                    {INDUSTRY_SECTORS.map((sector) => (
                      <option key={sector.id} value={sector.id}>
                        {sector.label}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleSaveIndustry}
                  disabled={isSavingIndustry || !selectedIndustry || !canEditIndustry()}
                  className="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isSavingIndustry ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Save Industry Preference
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Organization Memberships Card */}
          {isOrgLoaded && memberships.length > 0 && (
            <div className="card mb-6">
              <div className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Organization Memberships
                  </h2>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Your organization affiliations. Data visibility will be scoped to your organizations.
                </p>

                <div className="space-y-3">
                  {memberships.map((membership) => (
                    <div
                      key={membership.organization.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {membership.organization.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Role: {membership.role}
                        </p>
                      </div>
                      <div className="text-xs px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                        {membership.organization.slug}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default UserProfile